import { cn } from "@/lib/utils";
import { DashboardHeader } from "./DashboardHeader";
import { UploadScene, LeadsScene, FunnelScene, MetricsScene, ABTestScene } from "./scenes";
import type { SceneType } from "@/hooks/use-scene-autoplay";

interface DashboardMockProps {
  currentScene: SceneType;
  className?: string;
}

export function DashboardMock({ currentScene, className }: DashboardMockProps) {
  return (
    <div className={cn("relative z-10", className)}>
      {/* Secondary window - behind with offset (depth effect) */}
      <div
        className="absolute rounded-[20px] border border-white/20"
        style={{
          top: '12px',
          left: '12px',
          width: '100%',
          height: '100%',
          background: 'rgba(255, 255, 255, 0.50)',
          filter: 'blur(1px)',
          boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.08)'
        }}
        aria-hidden="true"
      />

      {/* Main window - front */}
      <div
        className="relative bg-white rounded-[20px] overflow-hidden w-[340px] sm:w-[400px] lg:w-[460px]"
        style={{
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.25), 0 12px 30px -10px rgba(0, 0, 0, 0.15)'
        }}
      >
        {/* Header */}
        <DashboardHeader />

        {/* Content area with scenes */}
        <div className="h-[340px] sm:h-[360px] lg:h-[400px] relative bg-white" style={{ clipPath: 'inset(0 round 0 0 20px 20px)' }}>
          {/* Upload Scene */}
          <div
            className={cn(
              "absolute inset-0 transition-all duration-300 ease-out",
              currentScene === "upload"
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-4 pointer-events-none"
            )}
          >
            <UploadScene isActive={currentScene === "upload"} />
          </div>

          {/* Leads Scene */}
          <div
            className={cn(
              "absolute inset-0 transition-all duration-300 ease-out",
              currentScene === "leads"
                ? "opacity-100 translate-x-0"
                : currentScene === "upload"
                  ? "opacity-0 translate-x-4 pointer-events-none"
                  : "opacity-0 -translate-x-4 pointer-events-none"
            )}
          >
            <LeadsScene isActive={currentScene === "leads"} />
          </div>

          {/* Funnel Scene */}
          <div
            className={cn(
              "absolute inset-0 transition-all duration-300 ease-out",
              currentScene === "funnel"
                ? "opacity-100 translate-x-0"
                : currentScene === "upload" || currentScene === "leads"
                  ? "opacity-0 translate-x-4 pointer-events-none"
                  : "opacity-0 -translate-x-4 pointer-events-none"
            )}
          >
            <FunnelScene isActive={currentScene === "funnel"} />
          </div>

          {/* Metrics Scene */}
          <div
            className={cn(
              "absolute inset-0 transition-all duration-300 ease-out",
              currentScene === "metrics"
                ? "opacity-100 translate-x-0"
                : currentScene === "upload" || currentScene === "leads" || currentScene === "funnel"
                  ? "opacity-0 translate-x-4 pointer-events-none"
                  : "opacity-0 -translate-x-4 pointer-events-none"
            )}
          >
            <MetricsScene isActive={currentScene === "metrics"} />
          </div>

          {/* A/B Test Scene */}
          <div
            className={cn(
              "absolute inset-0 transition-all duration-300 ease-out",
              currentScene === "abtest"
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-4 pointer-events-none"
            )}
          >
            <ABTestScene isActive={currentScene === "abtest"} />
          </div>
        </div>
      </div>
    </div>
  );
}
